import type { SessionUser } from './api/client'

/**
 * LE registre de navigation — la seule liste des écrans de la barre latérale.
 *
 * ─── Pourquoi un registre, et pas la liste écrite dans le composant ───
 *
 * `AppLayout` ne doit connaître AUCUN écran en particulier. Sans ce fichier, la
 * barre latérale devient l'endroit où l'on écrit « si l'utilisateur est admin,
 * afficher Organisation », puis « sauf sur cet écran », puis « mais pas pour un
 * viewer » — et le composant de chrome finit par porter les règles d'accès de
 * toute l'application, à l'endroit où personne ne les cherche.
 *
 * Ici, ajouter un écran = une vue, une route, UNE ligne ici. Le supprimer =
 * retirer les trois. C'est le même principe que `domain/registration.ts` côté
 * backend, appliqué à l'interface.
 *
 * Ce que ce registre ne contient PAS : « Mon profil » et « Paramètres de
 * l'organisation ». Ce sont des panneaux du menu du compte, pas des écrans — la
 * barre latérale liste des lieux de travail, on n'y « va » pas régler son thème.
 *
 * ─── Les deux périmètres ───
 *
 * `utilisateur` — le métier de l'application, ce que voit un client.
 * `superuser`   — l'exploitation de la PLATEFORME, réservée à l'organisation de
 *                 gestion (`organisations.is_hyperadmin`, migration 0006).
 *
 * Un seul jeu d'items est affiché à la fois : mélanger les deux dans une même
 * liste, c'est offrir à l'exploitant un menu où l'écran d'un client et l'annuaire
 * de tous les clients se ressemblent.
 */

export type Portee = 'utilisateur' | 'superuser'
export type RoleUtilisateur = SessionUser['role']

export interface EntreeNavigation {
  /** Nom de route Vue Router. C'est la clé : pas de chemin en dur ici. */
  nom: string
  /** Groupe d'affichage. Sert de clé i18n : `nav.groupe.<groupe>`. */
  groupe: string
  /**
   * Rendu en PIED de rail, hors des groupes.
   *
   * Réservé à ce qu'on consulte sans y travailler — la documentation d'API. Le
   * mêler aux écrans de travail le ferait chercher là où il n'est pas, et
   * l'isoler dans un groupe d'un seul item donnerait un titre de section pour
   * une ligne.
   */
  pied?: boolean
  portee: Portee
  /**
   * Rôles autorisés. `undefined` = tous les rôles authentifiés.
   *
   * Ce filtrage est un CONFORT D'AFFICHAGE, pas une sécurité : chaque route et
   * chaque requête sont contrôlées côté serveur. Une interface qui cache un
   * bouton n'a jamais protégé une donnée — mais montrer un écran qui répondra
   * 403 est une mauvaise expérience, et fait remonter des tickets.
   */
  roles?: readonly RoleUtilisateur[]
  /**
   * Tracés du pictogramme, SANS l'enveloppe `<svg>` (posée par le composant).
   *
   * Constantes littérales : le `v-html` du composant ne voit jamais de donnée
   * extérieure. Un pictogramme venu d'une API serait une injection HTML.
   */
  icone: string
}

export const NAVIGATION: readonly EntreeNavigation[] = [
  {
    // Suivi des enveloppes (E8-2) — tête du groupe « signature ». La liste est
    // la première chose qu'on cherche (« où en est mon envoi ? »), la création
    // le geste qu'on fait parfois — d'où cet ordre.
    nom: 'console.enveloppe.liste',
    groupe: 'signature',
    portee: 'utilisateur',
    icone: '<path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" />',
  },
  {
    // Envoi d'enveloppe (E8-1). Le détail reste SANS entrée de menu : il
    // s'atteint depuis la liste ou par redirection après envoi.
    nom: 'console.enveloppe.nouvelle',
    groupe: 'signature',
    portee: 'utilisateur',
    icone: '<path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4Z" />',
  },
  {
    // Catalogue des modèles de positions (E8-5) — en fin de groupe « signature » :
    // un outil de pré-remplissage, pas le geste quotidien. Aucun filtre de
    // rôles : le viewer LIT le catalogue (le serveur borne l'écriture).
    nom: 'console.modeles',
    groupe: 'signature',
    portee: 'utilisateur',
    // Tracé « gabarit » : un bandeau et deux cases — distinct de la mosaïque
    // du tableau de bord (quatre cases irrégulières), qui s'y ressemblerait trop.
    icone: '<rect x="3" y="3" width="18" height="7" rx="1" /><rect x="3" y="14" width="9" height="7" rx="1" /><rect x="16" y="14" width="5" height="7" rx="1" />',
  },
  {
    // Réglages de signature de l'organisation (E8-6) — après les modèles : un
    // endroit qu'on visite rarement, pas le geste quotidien. Aucun filtre de
    // rôles : les quatre LISENT (le viewer pré-remplit l'envoi avec), et le
    // serveur borne l'écriture (`peutGererReglagesSignature`, owner/admin).
    nom: 'console.reglages',
    groupe: 'signature',
    portee: 'utilisateur',
    // Tracé « curseurs de réglage » : trois lignes, trois curseurs — distincts
    // du rouage classique, qui évoquerait les préférences de COMPTE.
    icone: '<line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />',
  },
  {
    // Solde et journal des crédits (E13-3) — après les réglages : un endroit
    // qu'on consulte, pas le geste quotidien. Aucun filtre de rôles : les
    // quatre LISENT, et le serveur borne la bascule illimité à l'owner SEUL
    // (`peutReglerIllimite`).
    nom: 'console.credits',
    groupe: 'signature',
    portee: 'utilisateur',
    // Tracé « pièce de monnaie » : un cercle et son signe — distinct de la
    // carte bancaire, qui évoquerait un moyen de paiement à configurer.
    icone: '<circle cx="12" cy="12" r="9" /><path d="M12 7.5v9" /><path d="M15 9.5c-.6-1-1.7-1.5-3-1.5-1.8 0-3.2 1-3.2 2.3 0 3.1 6.4 1.3 6.4 4.4 0 1.3-1.4 2.3-3.2 2.3-1.3 0-2.4-.5-3-1.5" />',
  },
  {
    // Console d'horodatage (E14-2) — SON PROPRE groupe : l'horodatage n'est pas
    // la signature (dit la carte de l'epic : « l'horodatage n'est pas la
    // signature ») ; le mélanger dans le groupe « signature » ferait croire aux
    // deux que chacun est un cas particulier de l'autre. Aucun filtre de rôles :
    // les quatre LISENT (le dépôt et le renommage sont bornés côté SERVEUR).
    nom: 'console.horodatage.liste',
    groupe: 'horodatage',
    portee: 'utilisateur',
    // Tracé « sceau d'horloge » : un cadran et ses aiguilles — distinct du
    // document-liste des enveloppes, qui évoquerait un dossier à traiter.
    icone: '<circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />',
  },
  {
    // En PIED de rail : la documentation est un recours, là où on la cherche
    // quand le reste n'a pas suffi — pas un écran de travail. Aucun filtre de
    // rôles : documenter ne protège rien, et un viewer a autant besoin de
    // l'API qu'un admin.
    nom: 'developpeurs',
    groupe: 'pied',
    pied: true,
    portee: 'utilisateur',
    icone: '<path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" />',
  },

  // ---- Périmètre PLATEFORME ------------------------------------------------
  {
    nom: 'superuser.organisations',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M3 21h18" /><path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" /><path d="M15 21V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12" /><line x1="8" y1="8" x2="10" y2="8" /><line x1="8" y1="12" x2="10" y2="12" /><line x1="8" y1="16" x2="10" y2="16" />',
  },
  {
    nom: 'superuser.plans',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M20.59 13.41 11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z" /><circle cx="7.5" cy="7.5" r="1.5" />',
  },
  {
    nom: 'superuser.services',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" />',
  },
  {
    nom: 'superuser.service-etat',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M22 12h-4l-3 9L9 3l-3 9H2" />',
  },
]

/**
 * L'organisation de l'utilisateur est-elle celle de gestion, et son rôle
 * suffisant ?
 *
 * MIROIR de `canUseSuperuser` (backend `core/org/rules.ts`). Le serveur reste
 * l'autorité : cette fonction ne sert qu'à décider d'AFFICHER la bascule et à
 * éviter d'ouvrir un écran qui répondrait 403. Toute modification de la règle se
 * répercute des deux côtés — sinon l'un cache ce que l'autre autorise
 * (frustration), ou l'inverse (l'utilisateur découvre un écran en erreur).
 */
export function peutUtiliserSuperuser(utilisateur: SessionUser | null): boolean {
  if (utilisateur === null) return false
  if (!utilisateur.isHyperadmin) return false
  return utilisateur.role === 'owner' || utilisateur.role === 'admin'
}

/** Entrées visibles pour un utilisateur, dans un périmètre donné. */
export function entreesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  if (utilisateur === null) return []
  // Le périmètre plateforme n'existe pas pour qui n'y a pas droit : le demander
  // explicitement ne doit pas suffire à en obtenir les items.
  if (portee === 'superuser' && !peutUtiliserSuperuser(utilisateur)) return []

  return NAVIGATION.filter(
    (entree) =>
      entree.portee === portee &&
      (entree.roles === undefined || entree.roles.includes(utilisateur.role)),
  )
}

export interface GroupeNavigation {
  nom: string
  entrees: EntreeNavigation[]
}

/**
 * Entrées visibles, regroupées, dans l'ordre de DÉCLARATION du registre.
 *
 * L'ordre vient du fichier et non d'un tri alphabétique : « Tableau de bord »
 * avant « Éléments » est un choix éditorial, qu'un tri par nom casserait à la
 * première traduction.
 */
export function groupesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
): GroupeNavigation[] {
  const groupes: GroupeNavigation[] = []
  // Les entrées de PIED sortent des groupes : `entreesDePied` les sert à part.
  // Les laisser ici fabriquerait un groupe d'un seul item, avec son titre de
  // section — un intertitre pour une ligne.
  for (const entree of entreesVisibles(utilisateur, portee).filter((e) => e.pied !== true)) {
    let groupe = groupes.find((g) => g.nom === entree.groupe)
    if (groupe === undefined) {
      groupe = { nom: entree.groupe, entrees: [] }
      groupes.push(groupe)
    }
    groupe.entrees.push(entree)
  }
  return groupes
}

/**
 * Les entrées rendues en PIED de rail, dans l'ordre de déclaration.
 *
 * Même filtre de visibilité que le reste : une entrée de pied n'échappe pas
 * aux rôles ni au périmètre. Seul son EMPLACEMENT change.
 */
export function entreesDePied(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  return entreesVisibles(utilisateur, portee).filter((entree) => entree.pied === true)
}

/**
 * Premier écran atteignable d'un périmètre — sa page d'atterrissage.
 *
 * Utilisé au changement de périmètre : rester sur l'écran précédent laisserait
 * une barre latérale sans item actif, et l'utilisateur devant un contenu qui
 * n'appartient plus au menu affiché.
 */
export function accueilDuPerimetre(
  utilisateur: SessionUser | null,
  portee: Portee,
): string | null {
  // Les entrées de PIED ne sont pas des pages d'atterrissage : on n'ouvre pas
  // une application sur sa documentation.
  return entreesVisibles(utilisateur, portee).find((e) => e.pied !== true)?.nom ?? null
}

/** Périmètre auquel appartient une route, `null` si elle n'est pas dans la nav. */
export function porteeDeLaRoute(nomRoute: string | null | undefined): Portee | null {
  if (typeof nomRoute !== 'string') return null
  return NAVIGATION.find((entree) => entree.nom === nomRoute)?.portee ?? null
}
